import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import type { FamilyAmbassador } from '../../lib/types';
import { Sheet } from '../../ui/Sheet';
import { useParent } from '../ParentContext';

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const months = (n: number) => `${n} month${n === 1 ? '' : 's'}`;

/** Where an ambassador's discount stands on a family's subscription (#877), as Stripe has it. */
export function DiscountStatus({ a }: { a: FamilyAmbassador }) {
  const [label, tone, note] =
    a.status === 'applied'
      ? ['Discount applied', 'good', a.endsAt ? `until ${day(a.endsAt)}` : '']
      : a.status === 'used'
        ? ['Discount used', 'good', 'ran its months; full price now']
        : a.status === 'not-subscribed'
          ? ['Not subscribed yet', '', 'goes on at checkout']
          : a.status === 'founder'
            ? ['Founder discount instead', 'warn', 'Stripe takes one discount']
            : ['Not applied', 'warn', 'subscribed without it'];
  return (
    <span className="amb-status">
      <span className={`tag ${tone}`}>{label}</span>
      {note && <span className="muted small amb-note">{note}</span>}
    </span>
  );
}

/** On a family in Admin → Families (#877): the ambassador who sent it and whether their discount is on. */
export function FamilyAmbassadorLine({ familyId, a, onChanged }: { familyId: string; a: FamilyAmbassador; onChanged: () => void }) {
  const { toast } = useParent();
  const [busy, setBusy] = useState(false);
  async function apply() {
    setBusy(true);
    try {
      await api.post(`/api/admin/families/${familyId}/partner-discount`);
      toast({ text: `${a.name}’s discount is on the subscription now`, tone: 'good' });
      onChanged();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="amb-family">
      <span>
        Came through <strong>{a.name}</strong>{' '}
        <span className="muted small">
          ({a.kind === 'business' ? 'Business' : 'Brand Ambassador'} · code {a.code}) · signed up {day(a.joinedAt)} · {a.discountPct}% off for {months(a.discountMonths)}
          {a.earnedCents > 0 ? ` · ${a.name.split(' ')[0]} earned ${money(a.earnedCents)}` : ''}
        </span>
      </span>
      <DiscountStatus a={a} />
      {a.status === 'not-applied' && (
        <button type="button" className="btn small" onClick={apply} disabled={busy}>
          {busy ? 'Applying…' : 'Apply the discount'}
        </button>
      )}
    </div>
  );
}

type Row = FamilyAmbassador & { familyId: string; familyName: string; plan: string; billingStatus: string };

/** An ambassador's families in Admin → Partners (#877). */
export function PartnerFamiliesSheet({ partner, onClose }: { partner: { id: string; name: string; code: string }; onClose: () => void }) {
  const { toast } = useParent();
  const [rows, setRows] = useState<Row[] | null>(null);
  useEffect(() => {
    api.get<{ families: Row[] }>(`/api/admin/partners/${partner.id}/families`).then(
      (r) => setRows(r.families),
      (e) => toast({ text: (e as Error).message, tone: 'alert' }),
    );
  }, [partner.id, toast]);
  const paying = rows?.filter((r) => ['active', 'trialing', 'past_due'].includes(r.billingStatus)).length ?? 0;
  return (
    <Sheet title={`${partner.name}’s families`} onClose={onClose}>
      {!rows ? (
        <p className="muted">Loading…</p>
      ) : !rows.length ? (
        <p className="muted">No families have signed up with {partner.code} yet.</p>
      ) : (
        <div className="stack">
          <p className="muted small">
            {rows.length} {rows.length === 1 ? 'family' : 'families'} · {paying} paying · {rows.filter((r) => r.status === 'applied').length} with the discount on now ·{' '}
            {money(rows.reduce((n, r) => n + r.earnedCents, 0))} earned
          </p>
          <div className="table-scroll">
            <table className="bn-table">
              <thead>
                <tr>
                  <th>Family</th>
                  <th>Signed up</th>
                  <th>Plan</th>
                  <th>Discount</th>
                  <th>Earned</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.familyId}>
                    <td>
                      <strong>{r.familyName}</strong>
                    </td>
                    <td>{day(r.joinedAt)}</td>
                    <td>
                      {r.plan || '—'}
                      {r.billingStatus && <span className="muted small block">{r.billingStatus === 'trialing' ? 'trial' : r.billingStatus}</span>}
                    </td>
                    <td>
                      <DiscountStatus a={r} />
                    </td>
                    <td className="num">{money(r.earnedCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Sheet>
  );
}
